import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { ChevronDown } from 'lucide-react'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { useTeamContext } from '@/hooks/useTeamContext'
import { useApiReady } from '@/hooks/useApiReady'
import { useFreelancerProfile } from '@/hooks/queries/useProfile'
import { useFreelancerValidation } from '@/hooks/useFreelancerValidation'
import { isKYCComplete } from '@/utils/kycValidation'
import { isPortage } from '@/constants/legalEntityType'
import { USER_ROLES } from '@/config/permissions'
import { PortageInvitationBanner, shouldShowPortageBanner } from '@/components/portage/PortageInvitationBanner'

/**
 * Alerte affichée en haut du contenu quand le KYC documents d'un freelance n'est pas à jour.
 * Alignée sur le badge KYC vert (`isKYCComplete`) — pas sur la gate signature (IBAN/BIC).
 */
export default function KycBanner() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { effectiveRole } = useTeamContext()
  const apiReady = useApiReady()
  const [expanded, setExpanded] = useState(false)

  const isFreelancer = effectiveRole === USER_ROLES.FREELANCER
  const { data: freelancerProfile, isFetched: freelancerProfileFetched } = useFreelancerProfile(isFreelancer)
  const { errors } = useFreelancerValidation(isFreelancer)

  const missingDocuments = errors.missingDocuments ?? []
  const canExpand = missingDocuments.length > 0

  if (!isFreelancer || !apiReady || !freelancerProfileFetched) return null
  if (location.pathname.startsWith('/onboarding')) return null

  // Portage salarial : tant que la société de portage n'a pas confirmé, on affiche le bandeau
  // d'invitation à la place du KYC (le KYC du porté dépendra des documents de la société, lot ultérieur).
  const portageCompany = freelancerProfile?.portage_company
  const portageAffiliation = freelancerProfile?.portage_affiliation

  if (shouldShowPortageBanner(portageCompany, portageAffiliation) && freelancerProfile?.id) {
    return (
      <div className="px-4 pt-4">
        <PortageInvitationBanner
          company={portageCompany}
          affiliation={portageAffiliation}
          freelancerId={freelancerProfile.id}
        />
      </div>
    )
  }

  // Portage salarial : le KYC (documents) relève de la société de portage, pas du porté —
  // on n'affiche jamais le bandeau "KYC incomplet" pour un freelance en portage.
  if (isPortage(freelancerProfile?.legal_entity_type)) return null

  const kycComplete = !freelancerProfile ? true : isKYCComplete(freelancerProfile)
  if (kycComplete) return null

  const goToDocuments = () => navigate('/settings?tab=documents')

  return (
    <div className="px-4 pt-4">
      <Alert variant="warning" className="items-start gap-3 py-2.5">
        <div className="flex w-full flex-col ">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-1">
              <span className="text-sm">{t('kycBanner.message')}</span>
              {canExpand && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => setExpanded((open) => !open)}
                  aria-expanded={expanded}
                  aria-label={expanded ? t('kycBanner.toggleHide') : t('kycBanner.toggleShow')}
                  className="h-7 w-7 shrink-0 cursor-pointer text-yellow-900/80 hover:bg-yellow-100/60 hover:text-yellow-950"
                >
                  <ChevronDown
                    className={`h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''}`}
                  />
                </Button>
              )}
            </div>

            <Button
              size="sm"
              onClick={goToDocuments}
              className="h-7 shrink-0 bg-amber-500 px-3 text-xs text-white hover:bg-amber-600"
            >
              {t('kycBanner.action')}
            </Button>
          </div>

          {expanded && canExpand && (
            <ul className="list-disc  pl-4 text-sm text-yellow-900/90">
              {missingDocuments.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          )}
        </div>
      </Alert>
    </div>
  )
}
